import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Divider,
  SelectChangeEvent,
} from '@mui/material';

interface TickerSelectProps {
  tickers: string[];
  selectedTicker: string;
  onTickerChange: (ticker: string) => void;
  preferredTickers: string[];
  selectedPreferred: string | null;
  onPreferredChange: (preferred: string | null) => void;
  color: string;
}

export default function TickerSelect({
  tickers,
  selectedTicker,
  onTickerChange,
  preferredTickers,
  selectedPreferred,
  onPreferredChange,
  color,
}: TickerSelectProps) {
  const sortedTickers = [...tickers].sort();

  const handleChange = (event: SelectChangeEvent) => {
    onTickerChange(event.target.value);
    // Clear preferred when parent ticker changes
    onPreferredChange(null);
  };

  const handlePreferredClick = (preferred: string) => {
    // Toggle: if already selected, deselect (go back to parent)
    if (selectedPreferred === preferred) {
      onPreferredChange(null);
    } else {
      onPreferredChange(preferred);
    }
  };

  return (
    <Box>
      {/* Main REIT Ticker Section */}
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
        TICKER
      </Typography>
      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
        <InputLabel>REIT Ticker</InputLabel>
        <Select
          value={selectedTicker}
          label="REIT Ticker"
          onChange={handleChange}
        >
          <MenuItem value="">
            <em>Select REIT</em>
          </MenuItem>
          {sortedTickers.map((ticker) => (
            <MenuItem key={ticker} value={ticker}>
              {ticker}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedTicker && (
        <Box sx={{ mb: 1 }}>
          <Chip
            label={selectedTicker}
            size="small"
            onClick={() => onPreferredChange(null)}
            sx={{
              bgcolor: selectedPreferred ? 'transparent' : `${color}30`,
              border: selectedPreferred ? `1px solid ${color}50` : `2px solid ${color}`,
              borderRadius: 1,
              cursor: 'pointer',
              fontWeight: selectedPreferred ? 400 : 600,
              '&:hover': {
                bgcolor: `${color}20`,
              },
            }}
          />
        </Box>
      )}

      {/* Preferreds Section */}
      {selectedTicker && preferredTickers.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
            PREFERREDS
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {preferredTickers.map((pref) => (
              <Chip
                key={pref}
                label={pref}
                size="small"
                onClick={() => handlePreferredClick(pref)}
                sx={{
                  bgcolor: selectedPreferred === pref ? `${color}30` : 'transparent',
                  border: selectedPreferred === pref ? `2px solid ${color}` : `1px solid ${color}50`,
                  borderRadius: 1,
                  cursor: 'pointer',
                  fontWeight: selectedPreferred === pref ? 600 : 400,
                  justifyContent: 'flex-start',
                  '&:hover': {
                    bgcolor: `${color}20`,
                  },
                }}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
